'use client';

import React, { useEffect, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { COUNTRY_COOKIE, COUNTRY_SEGMENT_LIST } from '../lib/brand';

/**
 * Compact country dropdown for the EscapeTo header (US / CA / NZ / AU).
 *
 * Switching country rewrites the current path's country segment (or prefixes
 * one), stores the preference cookie the middleware uses for `/` redirects,
 * and hard-navigates so server-rendered metadata refreshes too.
 *
 * camping-australia never renders this (see Header.tsx) — no dropdown there.
 */

const COUNTRY_LABELS: Record<string, { flag: string; name: string }> = {
  us: { flag: '\u{1F1FA}\u{1F1F8}', name: 'United States' },
  ca: { flag: '\u{1F1E8}\u{1F1E6}', name: 'Canada' },
  nz: { flag: '\u{1F1F3}\u{1F1FF}', name: 'New Zealand' },
  au: { flag: '\u{1F1E6}\u{1F1FA}', name: 'Australia' },
  ar: { flag: '\u{1F1E6}\u{1F1F7}', name: 'Argentina' },
  cl: { flag: '\u{1F1E8}\u{1F1F1}', name: 'Chile' },
};

// Display order per the product spec: US / CA / NZ / AU / AR / CL.
const DISPLAY_ORDER = ['us', 'ca', 'nz', 'au', 'ar', 'cl'] as const;

function segmentFromPath(pathname: string | null): string | null {
  const seg = (pathname || '').split('/').filter(Boolean)[0]?.toLowerCase();
  return seg && (COUNTRY_SEGMENT_LIST as readonly string[]).includes(seg) ? seg : null;
}

export default function CountrySwitcher() {
  const t = useTranslations('country');
  const pathname = usePathname();
  const pathCountry = segmentFromPath(pathname);
  // SSR-safe: path segment first; cookie only after mount (not in SSR HTML).
  const [current, setCurrent] = useState<string>(pathCountry || 'us');
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (pathCountry) {
      setCurrent(pathCountry);
      return;
    }
    const m = document.cookie.match(/(?:^|;\s*)et_country=(us|au|ca|nz|ar|cl)\b/);
    if (m) setCurrent(m[1]);
  }, [pathCountry]);

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  const switchTo = (next: string) => {
    setOpen(false);
    if (next === current) return;
    document.cookie = `${COUNTRY_COOKIE}=${next}; path=/; max-age=31536000; samesite=lax`;
    const parts = (pathname || '/').split('/').filter(Boolean);
    if (parts.length && (COUNTRY_SEGMENT_LIST as readonly string[]).includes(parts[0]?.toLowerCase())) {
      parts[0] = next;
    } else {
      parts.unshift(next);
    }
    const search = typeof window !== 'undefined' ? window.location.search : '';
    window.location.assign(`/${parts.join('/')}${search}`);
  };

  const active = COUNTRY_LABELS[current] || COUNTRY_LABELS.us;

  return (
    <div ref={rootRef} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`${t(current)}. ${t('changeCountry')}`}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          padding: '6px 10px',
          borderRadius: '9999px',
          border: '1px solid #e5e7eb',
          background: '#fff',
          fontSize: '13px',
          fontWeight: 600,
          color: '#374151',
          cursor: 'pointer',
          lineHeight: 1,
        }}
      >
        <span aria-hidden style={{ fontSize: '15px' }}>{active.flag}</span>
        <span>{current.toUpperCase()}</span>
        <span aria-hidden style={{ fontSize: '9px', color: '#9ca3af' }}>▼</span>
      </button>

      {open && (
        <div
          role="listbox"
          aria-label={t('chooseCountry')}
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: '4px',
            minWidth: '180px',
            background: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: '10px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
            zIndex: 1000,
            padding: '4px 0',
          }}
        >
          {DISPLAY_ORDER.map((seg) => (
            <button
              key={seg}
              type="button"
              role="option"
              aria-selected={seg === current}
              onClick={() => switchTo(seg)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                width: '100%',
                padding: '8px 14px',
                border: 'none',
                background: seg === current ? '#f3f4f6' : 'transparent',
                fontSize: '13px',
                fontWeight: seg === current ? 700 : 500,
                color: '#374151',
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <span aria-hidden style={{ fontSize: '15px' }}>{COUNTRY_LABELS[seg].flag}</span>
              <span>{t(seg)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
